<template>
  <div>
    <planning-navigation v-if="(dynamic && ! view) || userConnected" :dynamic="dynamic" :view="view"
                         :planning="planning" :planning-view-id="planningViewId">
    </planning-navigation>

    <v-fade-transition hide-on-leave>
      <project-report v-if="uiPlanningStore.projectReportIsOpened" :planning="planning" :dynamic="dynamic"></project-report>
    </v-fade-transition>
    <v-fade-transition hide-on-leave>
      <div v-show="! uiPlanningStore.projectReportIsOpened" :id="(dynamic && ! view) ? 'planning' : 'viewer'">
        <planning-content :planning="planning" :dynamic="dynamic" :view="view" :options="{ elementDetails: { canSetPriority: true, canChangeLane: true, autoSave: true } }"></planning-content>
      </div>
    </v-fade-transition>
  </div>
</template>

<script>
  import useNavigationProjectsHistoryStore from '@/stores/navigation-projects-history';
  import useUiPlanningStore from '@/stores/ui/planning';
  import useUiSavingStore from '@/stores/ui/saving';
  import useUsersStore from '@/stores/users';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import useEventbusStore from '@/stores/eventbus';
  import usePlanningStore from '@/stores/planning/planning';
  import usePlanningTimelineStore from '@/stores/planning/timeline';
  import usePlanningConfigStore from '@/stores/planning/config';
  import useSubprojectsStore from '@/stores/subprojects';
  import useUndoRedoStore from '@/stores/undoredo';
  import useAutomations from '@/components/Automations/useAutomations';
  import ProjectSrv from '@/components/Projects/ProjectSrv';
  import Alert from '@/models/Alert';
  import Planning from '@/models/Planning';
  import ProjectReport from '@/components/ProjectReport/ProjectReport.vue';
  import PlanningNavigation from './PlanningNavigation.vue';
  import PlanningContent from './PlanningContent.vue';

  export default {
    components: {
      ProjectReport,
      PlanningNavigation,
      PlanningContent,
    },
    setup() {
      return {
        initAutomations: useAutomations().initAutomations,
      };
    },
    props: {
      dynamic: { type: Boolean, default: false },
      view: { type: Boolean, default: false },
    },
    data() {
      return {
        planning: {},
        planningId: this.view ? null : +this.$route.params.id,
        planningViewId: this.view ? +this.$route.params.id : null,
        tours: [],
        scrollValues: { planning: null, kanban: null },
        debouncedUpdateProject: null, // init in created
        planningIsUnmounted: false, // user navigated to another planning -> stop async callbacks
        navigationProjectsHistoryStore: useNavigationProjectsHistoryStore(),
        uiPlanningStore: useUiPlanningStore(),
        uiSavingStore: useUiSavingStore(),
        usersStore: useUsersStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
        eventbusStore: useEventbusStore(),
        planningStore: usePlanningStore(),
        planningTimelineStore: usePlanningTimelineStore(),
        planningConfigStore: usePlanningConfigStore(),
        subprojectsStore: useSubprojectsStore(),
        undoredoStore: useUndoRedoStore(),
      };
    },
    computed: {
      userConnected() {
        return this.usersStore.user.id > 0;
      },
      planningTitle() {
        return this.planning.getTitle?.();
      },
      isKanbanMode() {
        return this.uiPlanningStore.mode == 'kanban';
      },
      planningAndKanbanDisplayState() {
        const planningOrKanban = !! this.planning.visibleTimeline && ! this.uiPlanningStore.projectReportIsOpened;
        return { planning: planningOrKanban && ! this.isKanbanMode, kanban: planningOrKanban && this.isKanbanMode };
      },
    },
    watch: {
      planning(newVal) {
        this.usersStore.userPromise.then(() => {
          if (newVal && this.dynamic && ! this.view && this.usersAccessRightStore.config.automations) this.initAutomations(this.planning);
        });
      },
      planningTitle: _.debounce(function (newVal) {
        if (typeof newVal != 'string') return;
        // register navigation event
        this.navigationProjectsHistoryStore.register({ path: this.$route.path, rotoken: this.$route.query.rotoken, title: newVal, imgurl: this.planning.meta?.imgurl });
      }, 1000),
      planningAndKanbanDisplayState(newVals, oldVals) { // watch for display change to (re)store scroll value
        Object.keys(newVals).forEach((key) => {
          if (newVals[key] == oldVals[key]) return;
          if (! newVals[key]) { // hidding planning / kanban : store scroll value
            const tableWrapper = document.getElementById('table-wrapper');
            if (tableWrapper) this.scrollValues[key] = tableWrapper.scrollLeft;
          } else {
            setTimeout(() => {
              const tableWrapper = document.getElementById('table-wrapper');
              if (! tableWrapper) return;
              if (key == 'planning' && this.scrollValues[key] === null) {
                tableWrapper.scrollLeft = this.planning.visibleTimeline.presenttimeWidth - 0.25 * tableWrapper.clientWidth; // init scroll to current time
              } else {
                tableWrapper.scrollLeft = this.scrollValues[key] || 0; // restore scroll
              }
            });
          }
        });
      },
    },
    created() {
      this.loadProject();
      this.eventbusStore.register({ event: 'update-project', listener: 'planning', cb: this.updateProject });
      this.debouncedUpdateProject = _.debounce(function () { this.updateProject(); }, 30000);
    },
    beforeUnmount() {
      this.planningIsUnmounted = true;
      window.notificationsSrv.stopCheckingPlanningUpdates();
      this.debouncedUpdateProject.cancel();
      this.eventbusStore.remove({ event: 'update-project', listener: 'planning' });
      this.debouncedUpdateProject.cancel();
      this.tours.forEach((tour) => { if (tour) (tour.cancel || tour.stop)(); });
    },
    methods: {
      loadProject() {
        let request;
        if (this.view) {
          request = ProjectSrv.getView({ viewId: this.planningViewId, viewrotoken: this.$route.query.viewrotoken });
        } else {
          request = ProjectSrv.get(this.planningId, this.$route.query.rotoken);
        }
        request.then((content) => {
          if (this.planningIsUnmounted) return; // user left : do not update store or router
          this.planning = new Planning(content);
          if (! this.view && this.dynamic) {
            if (this.planning.meta && this.planning.meta.access_right == 'consult') {
              this.planning = {};
              const prettyname = this.$route.params?.prettyname;
              this.$router.replace({ path: `/viewer/${this.planningId}${prettyname ? `/${prettyname}` : ''}`, query: this.$route.query });
              return;
            }
          }
          this.uiPlanningStore.loadPlanningMode({ planningId: this.planning.id });
          // broadcast event
          this.onProjectLoaded();
          this.onProjectFirstLoad();
        }).catch((message) => {
          this.uiPlanningStore.errors = `${message || "Something went wrong."}`;
          window.status = "screenshotready";
        });
      },
      updateProject() {
        let request;
        if (this.view) {
          request = ProjectSrv.getView({ viewId: this.planningViewId, viewrotoken: this.$route.query.viewrotoken });
        } else {
          request = ProjectSrv.get(this.planningId, this.$route.query.rotoken);
        }
        request.then((content) => {
          if (this.planningIsUnmounted) return; // user left : do not update store or router
          const modifications = this.planning.update(content);
          this.planning.elements.filter(el => modifications.elements?.[el.id]).forEach((el) => { // add last modifier indication
            el.modificationUser = this.planning.meta.updated_by;
          });
          this.onProjectLoaded(); // this.$store.dispatch('planning/timeline/set', this.planning.timeline); might be enough
          this.eventbusStore.call({ event: 'project-updated', param: modifications });
          if (this.uiSavingStore.errorCount === 1) this.planningStore.save(); // try again once (resolve potential conflict)
        }).catch(() => {
          this.uiSavingStore.errorCount += 1; // display error message
        });
      },
      setScreenshotParams() {
        const urlparams = this.$route.query;
        if (! urlparams.snapshot) return;

        this.uiPlanningStore.snapshot = true;
        if (urlparams.mode == 'kanban') {
          this.uiPlanningStore.mode = 'kanban';
          // process steps
          if (urlparams.stepshidden) {
            urlparams.stepshidden.split(',').forEach((stepId) => {
              if (stepId == 'backlog') stepId = null;
              const index = (this.planningStore.planning.process.steps || []).findIndex(item => item.id === stepId);
              if (index > -1) this.planningStore.planning.process.steps.splice(index, 1);
            });
          }
        } else {
          // timeline
          const timeline = angular.copy(this.planningStore.planning.timeline);
          timeline.show_presenttime = false;
          if (urlparams.steps) timeline.steps = urlparams.steps.split(',');
          if (urlparams.hiddenbefore && moment(urlparams.hiddenbefore).isValid()) timeline.setHiddenBefore(urlparams.hiddenbefore);
          if (urlparams.hiddenafter && moment(urlparams.hiddenafter).isValid()) timeline.setHiddenAfter(urlparams.hiddenafter);
          if (urlparams.colwidth) timeline.colwidth = urlparams.colwidth;
          this.planningTimelineStore.set(timeline);
        }
        // lanes
        if (urlparams.laneshidden) {
          urlparams.laneshidden.split(',').forEach((laneId) => {
            const index = (this.planningStore.planning.lanes || []).findIndex(item => item.id === +laneId);
            if (index > -1) this.planningStore.planning.lanes.splice(index, 1);
          });
        }
      },
      onProjectFirstLoad() {
        // Check update notificatons
        window.notificationsSrv.checkPlanningUpdates({ planningId: this.planning.id }, (contentPatch) => {
          if (contentPatch?.length) {
            try {
              const modifications = this.planning.applyContentPatch(contentPatch);
              this.planning.elements.filter(el => modifications.elements[el.id]).forEach((el) => { // add last modifier indication
                el.modificationUser = this.planning.meta.updated_by;
              });
              this.planningTimelineStore.set(this.planning.timeline);
              this.eventbusStore.call({ event: 'project-updated', param: modifications });

              this.debouncedUpdateProject(); // reload full planning after 30s in case of sync errors
            } catch (e) {
              // applyContentPatch failed
              console.error('CATCHED ERROR', e);
              this.updateProject();
            }
          } else {
            this.updateProject();
          }
        });

        // Alerts
        setTimeout(() => {
          Alert.load(this.planningStore.planning);
        });

        // Recurrences
        if (! this.view && this.dynamic) {
          setTimeout(() => {
            this.usersStore.userPromise.then(() => {
              if (! this.usersAccessRightStore.isBusiness) return;
              this.planningConfigStore.updatePlanningRecurrences().then((needToSave) => {
                if (needToSave) this.planningStore.save();
              });
            });
          }, 3000); // not needed immediately, free some resources at start
        }

        // Screenshot mode
        if (this.$route.query.snapshot) {
          setTimeout(() => {
            Promise.all(this.subprojectsStore.promises).then(() => {
              this.setScreenshotParams();
              setTimeout(() => { window.status = "screenshotready"; }, 300);
            });
          });
        } else if (this.$route.query.mode == 'kanban') { // share link param
          this.uiPlanningStore.mode = 'kanban';
        }

        /*  GUIDED TOUR */
        if (this.dynamic && this.$route.query.tour) {
          this.$nextTick(() => {
            import('@/components/Tour/TourSrv').then((TourSrv) => {
              if (this.$route.query.tour == 'onboarding') {
                this.$router.replace({ query: {} });
                setTimeout(() => {
                  if (this._isDestroyed) return;
                  const tour = TourSrv.launchAddBubble();
                  if (tour) this.tours.push(tour);
                }, 5000);
              } else {
                if (this._isDestroyed) return;
                const tour = TourSrv.launch(this.$router, this.$route.query.tour);
                if (tour) this.tours.push(tour);
              }
            });
          });
        }

        /* Scroll to element */
        if (this.$route.query.el || this.$route.query.scrolltoel) {
          const elId = this.$route.query.el || this.$route.query.scrolltoel;
          const el = this.planning.elements.find(item => item.id == elId);
          const openElement = this.dynamic && !! this.$route.query.el; // save in a var before route is replaced
          if (el) setTimeout(() => { this.uiPlanningStore.scrollToEl({ el, open: openElement }); });
          this.$router.replace({ query: { ...this.$route.query, el: undefined, scrolltoel: undefined } });
        }
      },
      onProjectLoaded() {
        this.undoredoStore.reset();
        setTimeout(() => {
          if (this.planningIsUnmounted) return; // user left : do not update store or router
          this.planningStore.set({ planning: this.planning });
          this.planning = this.planningStore.planning;

          if (! this.usersStore.user.id) { // user (and business config) not loaded yet
            this.usersStore.userPromise.then(() => {
              if (this.usersAccessRightStore.isBusiness) {
                this.planningStore.set({ planning: this.planning }); // reload with business config
                this.planning = this.planningStore.planning;
              }
            });
          }
        });
      },
    },
  };
</script>
